import { Box, Button, SxProps, Theme, Typography } from "@outschool/backpack";
import {
  ClassroomQuery,
  TextMimeType,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { leaderPath } from "@outschool/routes";
import * as Time from "@outschool/time";
import { useLazyQuery } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  AllClassPostCommentsQueryQuery,
  AllClassPostCommentsQueryQueryVariables,
  ClassPostFragments,
  ClassPostType,
  ClassPost_ActivityFragment,
  ClassPost_LearnerFragment,
  ClassPost_MentionNamesFragment,
  ClassPost_SectionFragment,
  EditableMessageContent,
  EditableMessageContentSubmitType,
  ReplyButton,
  ReplyToProvider,
  allClassPostCommentsQuery,
  isClassPostFragment,
  useReplyToContext,
  useValidMentions,
} from "@outschool/ui-components-classroom";
import {
  AttachmentGallery,
  AvatarImage,
  DEFAULT_AVATAR,
  ErrorMessage,
  TextWithLinks,
  UserHeadshotImage,
} from "@outschool/ui-components-shared";
import {
  ConfirmationModal,
  Loading,
  Space,
  TrackingProperties,
} from "@outschool/ui-legacy-component-library";
import { RichTextView } from "@outschool/ui-rich-text-editor";
import { useBooleanState, usePreviousSet } from "@outschool/ui-utils";
import React from "react";

import * as Routes from "../../../shared/Routes";
import * as User from "../../../shared/User";
import Link from "../Link";
import EditClassPost from "./EditClassPost";
import SeeMoreCommentsLink from "./SeeMoreCommentsLink";

type ClassPostContextValue = {
  // Co-located fragments
  activity: ClassPost_ActivityFragment;
  section: ClassPost_SectionFragment;
  learner: ClassPost_LearnerFragment | null;
  mentionNames: ClassPost_MentionNamesFragment[];
  post: ClassPostType;

  // Configuration
  usersCanComment: boolean;
  canManage: boolean;
  usersCanPost: boolean;
  showSenderPhoto?: boolean;
  onDeleteComment?: (commentUid: string, postUid: string) => void;
};

const ClassPostContext = React.createContext<ClassPostContextValue | null>(
  null,
);

type ClassPostProps = ClassPostContextValue & {
  isNew?: boolean;
  // Callback
  onComment?: (
    postUid: string,
    text: string,
    attachments: any[],
    video: any,
    mentions: any[],
  ) => void;
  updateClassPost: (
    post: ClassPostType,
    text: string,
    textMimeType: string,
    attachments: ClassroomQuery["classroom"]["posts"][0]["messageContent"]["attachments"],
    video: ClassroomQuery["classroom"]["posts"][0]["messageContent"]["video"],
    publishAt: string,
  ) => Promise<void>;
};

export default function ClassPost({
  onComment = () => {},
  isNew = false,
  updateClassPost,
  ...classPostContext
}: ClassPostProps) {
  const {
    activity,
    learner,
    post,
    section,
    usersCanComment,
    canManage,
    usersCanPost,
    mentionNames,
    showSenderPhoto = true,
  } = classPostContext;

  // Updating class posts
  const handlePostUpdate = React.useCallback(
    async (
      text: $TSFixMe,
      textMimeType: $TSFixMe,
      attachments: $TSFixMe,
      video: $TSFixMe,
      publishAt: $TSFixMe,
    ) => {
      await updateClassPost(
        post,
        text,
        textMimeType,
        attachments,
        video,
        publishAt,
      );
    },
    [updateClassPost, post],
  );

  const { currentUser, userTimeZone } = useSession();
  const { t } = useTranslation("client\\components\\classroom\\ClassPost");

  const isPostOwner =
    currentUser && post.sender && post.sender.uid === currentUser.uid;
  const comments = isClassPostFragment(post)
    ? post.commentsPreview.comments
    : post.comments;

  const validMentionNames = useValidMentions(post, mentionNames);

  // only link to teacher profile on classes created by this teacher
  let senderImage = <UserHeadshotImage user={post.sender} size={45} />;
  if (post.sender.uid === activity.leader.uid) {
    senderImage = (
      <Link to={leaderPath(activity.leader.uid, activity.leader.leader_link)}>
        {senderImage}
      </Link>
    );
  }

  const [highlight, _setHighlight, clearHighlight] = useBooleanState(isNew);
  React.useEffect(() => {
    if (highlight) {
      const timer = setTimeout(() => {
        clearHighlight();
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [highlight, clearHighlight]);

  return (
    <ClassPostContext.Provider value={classPostContext}>
      <Box
        sx={(theme: Theme) => ({
          backgroundColor: highlight ? "#fffaea" : "common.white",
          transition: "background-color 2s ease",
          mb: "2em",
          py: "1em",
          paddingX: "1em",
          [theme.breakpoints.up("md")]: {
            paddingX: "2em",
          },
        })}
      >
        <Box
          flex
          sx={{
            flexDirection: "column",
          }}
        >
          <Box
            flex
            sx={{
              flexDirection: "row",
              marginBottom: "0.5em",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box flex>
              {showSenderPhoto && (
                <Box
                  sx={{
                    marginRight: "1em",
                  }}
                >
                  {senderImage}
                </Box>
              )}
              <Box>
                <Box
                  sx={{
                    color: "grey.500",
                  }}
                >
                  <Link
                    to={Routes.singlePostPath(section.uid, post.uid)}
                    style={{ color: "#6A7482" }}
                  >
                    {Time.formatDateTimeWithFullWeekday(
                      post.publishAt,
                      // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                      userTimeZone,
                    )}
                  </Link>
                </Box>
                <Box
                  sx={{
                    fontWeight: "fontWeightBold",
                  }}
                >
                  {User.shortenedLeaderName(post.sender.name)}
                </Box>
              </Box>
            </Box>
            {(isPostOwner || canManage) && usersCanPost && (
              <Box>
                <EditClassPost
                  formTitle={t("Edit announcement")}
                  buttonLabel={t("Edit…")}
                  primaryLabel={t("Update")}
                  messageContent={post.messageContent}
                  publishAt={post.publishAt}
                  onPost={handlePostUpdate}
                  mentionNames={validMentionNames}
                />
              </Box>
            )}
          </Box>
          <ClassPostContent
            allowRichText
            post={post}
            trackingLabel={"classroom"}
            trackingMetadata={{
              postUid: post.uid,
              userUid: currentUser?.uid,
              learnerUid: learner?.uid,
            }}
          />
          {comments && comments.length > 0 && (
            <Box
              sx={{
                marginBottom: "1em",
                borderBottom: "1px solid",
                borderColor: "grey.100",
              }}
            />
          )}
          <ReplyToProvider>
            <ClassPostComments />
            {(isPostOwner || canManage) && usersCanComment && (
              <AddComment onComment={onComment} mentionNames={mentionNames} />
            )}
          </ReplyToProvider>
        </Box>
      </Box>
    </ClassPostContext.Provider>
  );
}

ClassPost.fragments = ClassPostFragments;

interface ClassPostContentProps {
  post: ClassPostType;
  trackingLabel: string;
  trackingMetadata: TrackingProperties;
  sx?: SxProps;
  allowRichText?: boolean;
}

const truncatedLength = 300;
function ClassPostContent({
  post,
  trackingLabel,
  trackingMetadata,
  sx = {},
  allowRichText = false,
}: ClassPostContentProps) {
  const { currentUser } = useSession();
  const { activity, section, canManage } =
    React.useContext(ClassPostContext) ?? {};
  const { t } = useTranslation("client\\components\\classroom\\ClassPost");
  const isTeacherComment =
    // @ts-ignore TS(2532): Object is possibly 'undefined'.
    post.sender.uid === activity.user_uid ||
    // @ts-ignore TS(2532): Object is possibly 'undefined'.
    post.sender.uid === section.leader?.uid;
  const isPostOwner =
    currentUser && post.sender && post.sender.uid === currentUser.uid;
  const senderName = post.sender.name;

  const { messageContent } = post;
  const [expanded, setExpanded] = useBooleanState(
    !messageContent.text || messageContent.text.length < truncatedLength,
  );

  return (
    <Box
      sx={[
        {
          marginBottom: "0.5em",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box>
        {allowRichText &&
        messageContent.originalMimeType === TextMimeType.TextMarkdown ? (
          <RichTextView
            content={messageContent.htmlText}
            allowLinks={isTeacherComment}
            maxLines={expanded ? Infinity : 10}
            allowEmails={isTeacherComment || canManage || isPostOwner || false}
          />
        ) : (
          <pre
            style={{
              wordBreak: "break-word",
              margin: 0,
            }}
          >
            <TextWithLinks
              clickableLinks={isTeacherComment}
              text={messageContent.text?.trim()}
              maxTextLength={expanded ? Infinity : truncatedLength}
              hideEmails={!isTeacherComment && !canManage && !isPostOwner}
            />
          </pre>
        )}{" "}
        {!expanded && (
          <Button variant="link" onClick={setExpanded}>
            {t("Show more")}
          </Button>
        )}
      </Box>
      <AttachmentGallery
        video={messageContent.video}
        attachments={messageContent.attachments}
        trackingLabel={trackingLabel}
        trackingMetadata={trackingMetadata}
        senderName={senderName}
        // @ts-ignore TS(2322): Type 'boolean | null' is not assignable to type 'b... Remove this comment to see the full error message
        isPostOwner={isPostOwner}
      />
    </Box>
  );
}

function ClassPostComments() {
  const { post, section, onDeleteComment } =
    React.useContext(ClassPostContext) ?? {};
  const [commentToDelete, setCommentToDelete] = React.useState(null);
  const handleDeleteCommentConfirm = React.useCallback((comment: $TSFixMe) => {
    setCommentToDelete(comment);
  }, []);
  const handleDeleteComment = React.useCallback(
    (comment: $TSFixMe) => {
      setCommentToDelete(null);
      // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      onDeleteComment(comment.uid, post.uid);
    },
    [onDeleteComment, post],
  );
  const { t } = useTranslation("client\\components\\classroom\\ClassPost");

  const [fetchRestOfComments, { data, loading }] = useLazyQuery<
    AllClassPostCommentsQueryQuery,
    AllClassPostCommentsQueryQueryVariables
  >(allClassPostCommentsQuery, {
    variables: {
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      sectionUid: section.uid,
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      classPostUid: post.uid,
      plainText: false,
    },
  });

  let comments =
    data?.classroom.postByUid?.comments ??
    // @ts-ignore TS(2345): Argument of type 'ClassPostType | undefined' is no... Remove this comment to see the full error message
    (isClassPostFragment(post) ? post.commentsPreview.comments : post.comments);

  const seenCommentUids = usePreviousSet(comments.map(c => c.uid));

  return (
    <Box>
      {/* @ts-ignore TS(2345): Argument of type 'ClassPostType | undefined' is no... Remove this comment to see the full error message */}
      {isClassPostFragment(post) && !data && !loading && (
        <SeeMoreCommentsLink
          post={post}
          onClick={() => fetchRestOfComments()}
        />
      )}
      {loading && <Loading />}
      {comments.map(comment => (
        <ClassPostComment
          key={comment.uid}
          comment={comment}
          onDeleteConfirm={handleDeleteCommentConfirm}
          isNew={!seenCommentUids.has(comment.uid)}
        />
      ))}
      <ConfirmationModal
        buttonColor="danger"
        open={!!commentToDelete}
        confirmationLabel={t("Delete comment")}
        onConfirm={() => handleDeleteComment(commentToDelete)}
        onClose={() => setCommentToDelete(null)}
        hasCloseButton={false}
      />
    </Box>
  );
}

function ClassPostComment({ comment, onDeleteConfirm, isNew }: $TSFixMe) {
  const { currentUser, userTimeZone } = useSession();
  const { activity, section, learner, canManage, usersCanComment } =
    React.useContext(ClassPostContext) ?? {};
  const { t } = useTranslation("client\\components\\classroom\\ClassPost");

  // @ts-ignore TS(2532): Object is possibly 'undefined'.
  const isTeacherComment = comment.sender.uid === activity.user_uid;
  const senderImage = isTeacherComment ? (
    <UserHeadshotImage user={comment.sender} size={30} />
  ) : (
    // TODO: Unify how we show user avatars + remove the weird className passing from here
    <AvatarImage
      avatar={comment.sender.avatar || DEFAULT_AVATAR}
      width={30}
      height={30}
      name={comment.sender.name}
    />
  );

  const [highlight, _setHighlight, clearHighlight] = useBooleanState(isNew);
  React.useEffect(() => {
    if (highlight) {
      const timer = setTimeout(() => {
        clearHighlight();
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [highlight, clearHighlight]);

  const replyToUser = {
    id: comment.sender.uid,
    display: comment.sender.name || "",
  };

  // @ts-ignore TS(2531): Object is possibly 'null'.
  const isSender = currentUser.uid === comment.sender.uid;
  // @ts-ignore TS(2532): Object is possibly 'undefined'.
  const isTeacher = section.currentUserCanManage;
  const isReplyEnabled = usersCanComment && !isSender && isTeacher;

  return (
    <Box
      flex
      sx={{
        flexDirection: "row",
        backgroundColor: highlight ? "#fffaea" : undefined,
        transition: "background-color 2s ease",
      }}
    >
      <Box>{senderImage}</Box>
      <Box
        sx={{
          marginLeft: "0.5em",
          flexGrow: 1,
        }}
      >
        <Box flex>
          <Box
            sx={{
              fontWeight: "fontWeightBold",
            }}
          >
            {comment.sender.name?.trim() || t("Someone")}
          </Box>
          <Box
            sx={{
              color: "grey.500",
              marginLeft: "0.5em",
            }}
          >
            {/* @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message */}
            {Time.formatDateTimeWithFullWeekday(comment.sendAt, userTimeZone)}
          </Box>
          {canManage && (
            <Box
              flex
              sx={{
                marginLeft: "0.5em",
                alignItems: "center",
              }}
            >
              <Button
                variant="link"
                onClick={() => onDeleteConfirm(comment)}
                title={t("Delete Comment")}
              >
                {/* eslint-disable-next-line i18next/no-literal-string*/}
                <Typography
                  variant="inherit"
                  sx={{
                    color: "error.500",
                  }}
                >
                  ×
                </Typography>
              </Button>
            </Box>
          )}
        </Box>
        <ClassPostContent
          post={comment}
          sx={{
            padding: "0.5em",
            backgroundColor: isSender ? "rgba(43, 167, 206, 0.10)" : "#F8F8F9",
            borderRadius: "6px",
          }}
          trackingLabel="classroom_comment"
          trackingMetadata={{
            commentUid: comment.uid,
            userUid: currentUser?.uid,
            learnerUid: learner?.uid,
          }}
        />
        {isReplyEnabled && <ReplyButton replyToUser={replyToUser} />}
      </Box>
    </Box>
  );
}

function AddComment({
  onComment,
  mentionNames,
}: {
  onComment: ClassPostProps["onComment"];
  mentionNames: ClassPost_MentionNamesFragment[];
}) {
  const [addCommentError, setAddCommentError] = React.useState<Error | null>(
    null,
  );
  const { messageContents, clearReplyTo } = useReplyToContext();
  const { post, learner, canManage } = React.useContext(ClassPostContext) ?? {};
  const { t } = useTranslation("client\\components\\classroom\\ClassPost");
  const handleCommentSubmit = async ({
    text,
    attachments,
    video,
    mentions,
  }: EditableMessageContentSubmitType) => {
    if (!post || !onComment) {
      return false;
    }
    setAddCommentError(null);
    try {
      await onComment(post.uid, text, attachments!, video, mentions!);
      clearReplyTo();
      return true;
    } catch (error) {
      setAddCommentError(error);
      return false;
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        marginTop: "1em",
      }}
    >
      {addCommentError && <ErrorMessage value={addCommentError} />}
      <EditableMessageContent
        expandedRows={10}
        onSubmit={handleCommentSubmit}
        collapsedUntilFocused={true}
        messageContent={messageContents}
        inClassContext={true}
        classMentionsData={mentionNames}
        placeholder={
          canManage || !learner
            ? t`Write a comment`
            : t("Write a comment as {{learner.name}}", {
                learnerName: learner.name,
              })
        }
        renderButton={({ submit, disabled, reset }) => {
          return (
            <Box
              flex
              sx={{
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                disabled={disabled}
                onClick={async () => {
                  const success = await submit();
                  if (success) {
                    reset();
                  }
                }}
              >
                {t("Send")}
              </Button>
              <Space x="medium" />
              <Button
                variant="link"
                onClick={() => {
                  reset();
                  setAddCommentError(null);
                }}
              >
                {t("Cancel")}
              </Button>
            </Box>
          );
        }}
      />
    </Box>
  );
}
